import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Plus, MagnifyingGlass, ArrowCounterClockwise, FilePdf,
  Trash, PencilSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import YearMonthNav from '../components/YearMonthNav';

import { API } from '../config/api';

const statusColors = {
  bozza: 'bg-gray-100 text-gray-700 border-gray-200',
  emessa: 'bg-blue-100 text-blue-700 border-blue-200',
  applicata: 'bg-green-100 text-green-700 border-green-200',
};

export default function CreditNotePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/credit-notes`);
      setList(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error fetching credit notes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('creditNote.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/credit-notes/${id}`);
      toast.success(t('creditNote.deleted'));
      fetchData();
    } catch (err) {
      toast.error(t('creditNote.deleteError'));
    }
  };

  const handleDownloadPdf = async (item) => {
    try {
      const response = await axios.get(`${API}/credit-notes/${item.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${item.nc_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error(t('creditNote.pdfError'));
    }
  };

  const filtered = (list || []).filter(d => {
    const matchSearch = d.nc_number?.toLowerCase().includes(search.toLowerCase()) ||
      d.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      d.reference_document?.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    const dateStr = d.date || '';
    if (dateStr.length < 7) return false;
    const year = parseInt(dateStr.substring(0, 4));
    if (year !== selectedYear) return false;
    if (selectedMonth !== null) {
      const month = parseInt(dateStr.substring(5, 7)) - 1;
      if (month !== selectedMonth) return false;
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A] flex items-center gap-2" data-testid="credit-note-title">
            <ArrowCounterClockwise size={28} weight="duotone" className="text-[#C53030]" />
            {t('creditNote.title')}
          </h1>
          <p className="text-[#4A5568] mt-1">{t('creditNote.subtitle')}</p>
        </div>
        <Button
          onClick={() => navigate('/note-credito/new')}
          className="bg-[#C53030] hover:bg-[#9B2C2C] text-white shrink-0"
          data-testid="new-credit-note-button"
        >
          <Plus size={20} className="mr-2" />
          {t('creditNote.newCreditNote')}
        </Button>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
        <Input
          placeholder={t('creditNote.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 border-[#E2E8F0]"
          data-testid="credit-note-search"
        />
      </div>

      <YearMonthNav
        items={list}
        dateField="date"
        totalField="total"
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
      />

      {loading ? (
        <div className="text-center py-12 text-[#4A5568]">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="text-center py-16">
            <ArrowCounterClockwise size={48} className="mx-auto text-[#CBD5E0] mb-4" weight="duotone" />
            <h3 className="font-medium text-[#0A0F1A] mb-2">{t('creditNote.noCreditNotes')}</h3>
            <p className="text-sm text-[#4A5568] mb-4">{t('creditNote.createFirst')}</p>
            <Button
              onClick={() => navigate('/note-credito/new')}
              className="bg-[#C53030] hover:bg-[#9B2C2C] text-white"
              data-testid="create-first-credit-note"
            >
              <Plus size={20} className="mr-2" />
              {t('creditNote.newCreditNote')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <Card key={item.id} className="border-[#E2E8F0] hover:border-[#CBD5E0] transition-colors" data-testid={`credit-note-card-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-outfit font-bold text-[#C53030]">{item.nc_number}</span>
                      <Badge className={`text-[10px] ${statusColors[item.status] || 'bg-gray-100 text-gray-600'}`}>
                        {t(`creditNote.statusLabels.${item.status}`) || item.status}
                      </Badge>
                      {item.reference_document && (
                        <span className="text-[10px] text-[#4A5568] bg-[#F4F6F8] px-2 py-0.5 rounded">
                          Rif: {item.reference_document}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#0A0F1A] font-medium">{item.client_name}</p>
                    <p className="text-xs text-[#4A5568]">
                      {formatDate(item.date)} | {(item.items || []).length} {t('creditNote.items')} | -{formatCurrency(item.total)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => handleDownloadPdf(item)} data-testid={`credit-note-pdf-${item.id}`}>
                      <FilePdf size={18} className="text-red-500" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/note-credito/${item.id}/edit`)} data-testid={`credit-note-edit-${item.id}`}>
                      <PencilSimple size={18} className="text-[#4A5568]" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} data-testid={`credit-note-delete-${item.id}`}>
                      <Trash size={18} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

